import React, { useState } from 'react';
import useSWR from 'swr';
import {
  Container, Paper, Typography, Box, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, CircularProgress, Alert,
  Tabs, Tab, Chip, Stack, TextField, MenuItem,
} from '@mui/material';
import { fetcher } from 'shared/helpers/axios';
import BackButton from 'components/common/buttons/BackButton';
import EngagementPanel from './EngagementPanel';
import AsOf from './AsOf';
import PageContainer from 'components/common/layout/PageContainer';

const StatCard = ({ label, value, sub }) => (
  <Paper sx={{ px: 3, py: 2, flex: 1, minWidth: { xs: '45%', sm: 'auto' }, textAlign: 'center' }}>
    <Typography variant="h4" fontWeight={700}>{value ?? '–'}</Typography>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
  </Paper>
);

const AdminAnalyticsPage = () => {
  const [tab, setTab] = useState(0);
  const [days, setDays] = useState('7');

  const SUMMARY_URL = '/interact/access-logs/summary/';
  const { data: summary, error: summaryError, isLoading: summaryLoading, mutate: mutateSummary } = useSWR(
    SUMMARY_URL,
    fetcher,
    { revalidateOnFocus: false }
  );

  const { data: logsData, error: logsError, isLoading: logsLoading } = useSWR(
    `/interact/access-logs/?days=${days}`,
    fetcher
  );

  const logs = logsData?.results || logsData || [];

  if (summaryLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (summaryError) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">Failed to load analytics. Make sure you have admin access.</Alert>
      </Container>
    );
  }

  return (
    <PageContainer width="wide">
      <Box display="flex" alignItems="center" gap={1} mb={3}>
        <BackButton position="inline" />
        <Typography variant="h4" fontWeight={800} sx={{ flex: 1 }}>Site Analytics</Typography>
        <AsOf asOf={summary?.as_of} url={SUMMARY_URL} mutate={mutateSummary} />
      </Box>

      {/* Summary cards */}
      <Stack direction="row" spacing={2} mb={3} flexWrap="wrap">
        <StatCard label="Requests (24h)" value={summary?.last_24h} />
        <StatCard label="Requests (7d)" value={summary?.last_7d} />
        <StatCard label="Requests (30d)" value={summary?.last_30d} />
        <StatCard label="Unique IPs (24h)" value={summary?.unique_ips_24h} />
        <StatCard label="Active Users (24h)" value={summary?.unique_users_24h} />
        <StatCard label="Active Users (7d)" value={summary?.unique_users_7d} />
      </Stack>

      {/* Tabs */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" allowScrollButtonsMobile sx={{ mb: 2 }}>
        <Tab label="Requests per Day" />
        <Tab label="Top IPs" />
        <Tab label="Top Paths" />
        <Tab label="Top Users" />
        <Tab label="Access Logs" />
        <Tab label="Engagement" />
      </Tabs>

      {/* Engagement: most engaged users / IPs and what they engage with */}
      {tab === 5 && <EngagementPanel />}

      {/* Requests per Day */}
      {tab === 0 && summary?.requests_per_day && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell align="right">Requests</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {summary.requests_per_day.slice().reverse().map(row => (
                <TableRow key={row.date} hover>
                  <TableCell>{row.date}</TableCell>
                  <TableCell align="right">
                    <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
                      {row.count}
                      <Box
                        sx={{
                          height: 8,
                          width: `${Math.min((row.count / Math.max(...summary.requests_per_day.map(r => r.count), 1)) * 120, 120)}px`,
                          backgroundColor: 'primary.main',
                          borderRadius: 1,
                          opacity: 0.6,
                        }}
                      />
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Top IPs */}
      {tab === 1 && summary?.top_ips && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>IP Address</TableCell>
                <TableCell align="right">Requests (7d)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {summary.top_ips.map((row, i) => (
                <TableRow key={row.ip_address} hover>
                  <TableCell>{i + 1}</TableCell>
                  <TableCell>
                    <Chip label={row.ip_address} size="small" variant="outlined" sx={{ fontFamily: 'monospace' }} />
                  </TableCell>
                  <TableCell align="right">{row.count}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Top Paths */}
      {tab === 2 && summary?.top_paths && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Path</TableCell>
                <TableCell align="right">Requests (7d)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {summary.top_paths.map((row, i) => (
                <TableRow key={row.path} hover>
                  <TableCell>{i + 1}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{row.path}</TableCell>
                  <TableCell align="right">{row.count}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Top Users */}
      {tab === 3 && summary?.top_users && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>User</TableCell>
                <TableCell align="right">Requests (7d)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {summary.top_users.map((row, i) => (
                <TableRow key={row.user__id} hover>
                  <TableCell>{i + 1}</TableCell>
                  <TableCell>
                    {row.user__first_name} {row.user__last_name}
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      @{row.user__username}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{row.count}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Access Logs */}
      {tab === 4 && (
        <>
          <Box mb={2}>
            <TextField
              select
              size="small"
              label="Time Range"
              value={days}
              onChange={e => setDays(e.target.value)}
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="1">Last 24 hours</MenuItem>
              <MenuItem value="7">Last 7 days</MenuItem>
              <MenuItem value="30">Last 30 days</MenuItem>
              <MenuItem value="90">Last 90 days</MenuItem>
            </TextField>
          </Box>

          {logsLoading ? (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
          ) : logsError ? (
            <Alert severity="error">Failed to load access logs</Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 600 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Timestamp</TableCell>
                    <TableCell>IP</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Method</TableCell>
                    <TableCell>Path</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.slice(0, 200).map(log => (
                    <TableRow key={log.id} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant="caption">
                          {new Date(log.timestamp).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={log.ip_address} size="small" variant="outlined" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{log.username || '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={log.method}
                          size="small"
                          color={log.method === 'GET' ? 'primary' : log.method === 'POST' ? 'success' : 'warning'}
                          variant="outlined"
                          sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem', maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {log.path}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={log.status_code}
                          size="small"
                          color={log.status_code < 300 ? 'success' : log.status_code < 400 ? 'info' : 'error'}
                          variant="outlined"
                          sx={{ fontSize: '0.75rem' }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </PageContainer>
  );
};

export default AdminAnalyticsPage;
